import { useEffect, useState } from "react";
import { Users, Plus, Edit, Trash2, Shield, ShieldCheck, ShieldAlert, Eye } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { getAdminUsers, createAdminUser, updateAdminUser, deleteAdminUser } from "@/lib/api";
import { formatDateTime } from "@/lib/dateUtils";

const roleColors = {
  super_admin: "bg-purple-500/20 text-purple-600 border-purple-500/30",
  admin: "bg-green-500/20 text-green-600 border-green-500/30",
  editor: "bg-blue-500/20 text-blue-600 border-blue-500/30",
  sport_eldorado_admin: "bg-orange-500/20 text-orange-600 border-orange-500/30",
  viewer: "bg-gray-500/20 text-gray-600 border-gray-500/30",
};

const roleIcons = {
  super_admin: ShieldAlert,
  admin: ShieldCheck,
  editor: Shield,
  sport_eldorado_admin: Users,
  viewer: Eye,
};

const roleDescriptions = {
  super_admin: "Volledige toegang tot alle functies inclusief gebruikersbeheer",
  admin: "Kan alle content beheren en bestellingen bekijken",
  editor: "Kan content aanmaken en bewerken (wedstrijden, nieuws, spelers, etc.)",
  sport_eldorado_admin: "Kan alleen Sport Eldorado content beheren (galerij, activiteiten, team)",
  viewer: "Alleen-lezen toegang tot admin panel",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    role: "editor"
  });
  const [currentUserRole, setCurrentUserRole] = useState("");

  const fetchUsers = async () => {
    try {
      const response = await getAdminUsers();
      setUsers(response.data);
      
      // Get current user role from localStorage
      const role = localStorage.getItem("adminRole") || "viewer";
      setCurrentUserRole(role);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Je hebt geen toestemming om gebruikers te beheren");
      } else {
        console.error("Error fetching users:", error);
        toast.error("Kon gebruikers niet laden");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return "Nooit";
    return formatDateTime(dateStr);
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        password: "",
        role: user.role
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: "",
        email: "",
        password: "",
        role: "editor"
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        await updateAdminUser(editingUser.id, updateData);
        toast.success("Gebruiker succesvol bijgewerkt");
      } else {
        if (!formData.password) {
          toast.error("Wachtwoord is verplicht voor nieuwe gebruikers");
          return;
        }
        await createAdminUser(formData);
        toast.success("Gebruiker succesvol aangemaakt");
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error("Error saving user:", error);
      toast.error(error.response?.data?.detail || "Failed to save user");
    }
  };

  const handleDelete = async (user) => {
    if (!confirm(`Weet je zeker dat je gebruiker "${user.username}" wilt verwijderen?`)) return;
    
    try {
      await deleteAdminUser(user.id);
      toast.success("Gebruiker succesvol verwijderd");
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.response?.data?.detail || "Kon gebruiker niet verwijderen");
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-slate-200 dark:bg-slate-700 rounded" />
          <div className="h-96 bg-slate-200 dark:bg-slate-700 rounded-lg" />
        </div>
      </div>
    );
  }

  if (currentUserRole !== "super_admin") {
    return (
      <div className="p-6" data-testid="admin-users-page">
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardContent className="pt-6 text-center py-12">
            <ShieldAlert className="h-16 w-16 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Toegang Geweigerd</h2>
            <p className="text-slate-600 dark:text-slate-400">
              Alleen Super Admins kunnen gebruikers beheren. Neem contact op met je administrator voor toegang.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Gebruikersbeheer</h1>
            <p className="text-slate-600 dark:text-slate-400">Beheer admin gebruikers en rechten</p>
          </div>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-blue-600 hover:bg-blue-700" data-testid="add-user-btn">
          <Plus className="h-4 w-4 mr-2" />
          Gebruiker Toevoegen
        </Button>
      </div>

      {/* Role Descriptions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {Object.entries(roleDescriptions).map(([role, description]) => {
          const Icon = roleIcons[role];
          return (
            <Card key={role} className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                  <Badge className={roleColors[role]}>
                    {role.replace("_", " ").toUpperCase()}
                  </Badge>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400">{description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Users Table */}
      <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-200 dark:border-slate-700">
                <TableHead className="text-slate-600 dark:text-slate-400">Gebruikersnaam</TableHead>
                <TableHead className="text-slate-600 dark:text-slate-400">Email</TableHead>
                <TableHead className="text-slate-600 dark:text-slate-400">Rol</TableHead>
                <TableHead className="text-slate-600 dark:text-slate-400">Status</TableHead>
                <TableHead className="text-slate-600 dark:text-slate-400">Laatste Login</TableHead>
                <TableHead className="text-slate-600 dark:text-slate-400">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.length > 0 ? (
                users.map((user) => {
                  const RoleIcon = roleIcons[user.role] || Shield;
                  return (
                    <TableRow 
                      key={user.id} 
                      className="border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50"
                      data-testid={`user-row-${user.id}`}
                    >
                      <TableCell className="font-medium text-slate-900 dark:text-white">
                        {user.username}
                      </TableCell>
                      <TableCell className="text-slate-600 dark:text-slate-400">
                        {user.email}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${roleColors[user.role]} flex items-center gap-1 w-fit`}>
                          <RoleIcon className="h-3 w-3" />
                          {user.role.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={user.is_active 
                          ? "bg-green-500/20 text-green-600 border-green-500/30" 
                          : "bg-red-500/20 text-red-600 border-red-500/30"
                        }>
                          {user.is_active ? "Actief" : "Uitgeschakeld"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600 dark:text-slate-400 text-sm">
                        {user.last_login ? formatDate(user.last_login) : "Nooit"}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleOpenDialog(user)}
                            className="border-slate-200 dark:border-slate-600"
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleDelete(user)}
                            className="border-red-200 dark:border-red-800 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-slate-500 dark:text-slate-400">
                    Geen gebruikers gevonden
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-slate-900 dark:text-white">
              {editingUser ? "Gebruiker Bewerken" : "Nieuwe Gebruiker"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Gebruikersnaam *</Label>
              <Input
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                placeholder="Voer gebruikersnaam in"
                className="bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600"
                data-testid="user-username"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="Voer email in"
                className="bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600"
                data-testid="user-email"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">
                Wachtwoord {editingUser ? "(laat leeg om huidig te behouden)" : "*"}
              </Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder={editingUser ? "Voer nieuw wachtwoord in" : "Voer wachtwoord in"}
                className="bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600"
                data-testid="user-password"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-700 dark:text-slate-300">Rol *</Label>
              <Select 
                value={formData.role} 
                onValueChange={(value) => setFormData({...formData, role: value})}
              >
                <SelectTrigger className="bg-white dark:bg-slate-700 border-slate-200 dark:border-slate-600" data-testid="user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="sport_eldorado_admin">Sport Eldorado Admin</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {roleDescriptions[formData.role]}
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuleren
            </Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-user-btn">
              {editingUser ? "Gebruiker Bijwerken" : "Gebruiker Aanmaken"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
